import { useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import type { AutoExecutionHistoryEntry } from '@ebalance/proto/bffuser/v1/workflow_automation_history_model_pb'
import { listAutoExecutionHistory } from '@ebalance/proto/bffuser/v1/workflow_automation_service-WorkflowAutomationService_connectquery'
import { ListAutoExecutionHistoryRequestSchema } from '@ebalance/proto/bffuser/v1/workflow_automation_service_pb'
import { keepPreviousData } from '@tanstack/react-query'

export type AutoExecutionHistoryState =
  | { status: 'loading' }
  | { status: 'error'; retry: () => void }
  | { status: 'empty' }
  /** total > 0 なのにこの page は空 (page が範囲外)。 呼び出し側が lastPage へ戻す。 */
  | { status: 'outOfRange'; lastPage: number }
  | {
      status: 'ready'
      entries: AutoExecutionHistoryEntry[]
      totalCount: number
      /** entries が属する page。 ページ送りの取得中は要求中の page でなく、 表示中の行の page を返す */
      shownPage: number
      /** ページ送りで次の page を取得中 (前の page を残している) */
      isFetching: boolean
    }

/**
 * 自動実行履歴の 1 page を取得し、 画面が分岐する状態に判別する (AC-AEH-005 / 008 / 009)。
 * retry の回数は共通の retry policy に任せ、 ここでは上書きしない。
 */
export function useAutoExecutionHistory(args: {
  page: number
  pageSize: number
}): AutoExecutionHistoryState {
  const result = useQuery(
    listAutoExecutionHistory,
    create(ListAutoExecutionHistoryRequestSchema, args),
    { throwOnError: false, placeholderData: keepPreviousData },
  )
  // 取得中 (placeholder) は前の page の行を残すので、 行が属する page を別に持つ。
  const [shownPage, setShownPage] = useState(args.page)
  if (
    !result.isPlaceholderData &&
    result.data !== undefined &&
    shownPage !== args.page
  ) {
    setShownPage(args.page)
  }
  if (result.isError) {
    return { status: 'error', retry: () => void result.refetch() }
  }
  if (result.data === undefined) return { status: 'loading' }
  const { entries, totalCount } = result.data
  if (totalCount === 0) return { status: 'empty' }
  if (entries.length === 0) {
    return {
      status: 'outOfRange',
      lastPage: Math.max(1, Math.ceil(totalCount / args.pageSize)),
    }
  }
  return {
    status: 'ready',
    entries,
    totalCount,
    shownPage,
    isFetching: result.isFetching,
  }
}
